import { Suspense, useCallback, useEffect, useState } from 'react';
import { ActivityIndicator, ScrollView } from 'react-native';
import {
  Divider,
  Flex,
  PrimaryButton,
  StackScreen,
  Typography,
  useSafeArea,
  useScreenSize,
} from '@banhala/ui-kit';
import { bridge } from 'shared/src/utils';
import { useHandleBackPressed, useReportTTFD } from 'shared/src/hooks';
import { useMyInfo } from 'shared/src/queries';
import { CartIcon, SearchIcon } from 'shared/src/component';
import { AnalyticsScreen } from 'shared/src/context';
import { useMyPageRoute } from 'mypage/src/hooks/useMyPageRoute';
import { BirthdaySelector } from './components/BirthdaySelector';
import { useLogCompleteEditMyInfo, useLogViewEvent } from './hooks';
import { useUpdateMyInfo } from './queries';

const MyInfoContent = ({ onClose }: { onClose: () => void }) => {
  const route = useMyPageRoute<'MyInfo'>();
  const from = route.params?.from;
  const { bottom } = useSafeArea();

  const { data: myInfo, status: myInfoStatus } = useMyInfo();
  const [birthday, setBirthday] = useState<string | null>(
    myInfo?.birth_date ?? null,
  );
  useEffect(() => {
    if (myInfoStatus === 'success') {
      setBirthday(myInfo?.birth_date ?? null);
    }
  }, [myInfo?.birth_date, myInfoStatus]);

  const { mutate: updateMyInfo, isPending } = useUpdateMyInfo();
  const logComplete = useLogCompleteEditMyInfo();

  useReportTTFD();
  useLogViewEvent({ from, birth: myInfo?.birth_date ?? null });

  const handlePressBirthday = useCallback(async () => {
    const next = await bridge.openDatePicker({
      initialDate: birthday,
      minDate: BIRTHDAY_MIN_DATE,
      maxDate: formatYyyyMmDd(new Date()),
    });
    if (next != null) {
      setBirthday(next);
    }
  }, [birthday]);

  const handlePressSave = useCallback(() => {
    if (isPending) return;
    updateMyInfo(
      { birth_date: birthday },
      {
        onSuccess: () => {
          logComplete({ from, birth: birthday });
          onClose();
        },
        onError: () => {
          bridge.toast('내 정보 저장에 실패했어요');
        },
      },
    );
  }, [birthday, from, isPending, logComplete, onClose, updateMyInfo]);

  return (
    <Flex flex={1}>
      <ScrollView
        style={{ flex: 1 }}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ padding: 16 }}
      >
        <Typography type="body2" color="content_secondary">
          생년월일을 입력하시면 맞춤 상품을 추천받으실 수 있습니다
        </Typography>
        <Flex padding={{ top: 24 }}>
          <Typography type="body2" color="content_tertiary">
            생년월일을 선택해주세요
          </Typography>
        </Flex>
        <Flex padding={{ top: 12 }}>
          <BirthdaySelector birthday={birthday} onPress={handlePressBirthday} />
        </Flex>
      </ScrollView>

      <Divider size="small" />
      <Flex
        padding={{
          top: 16,
          horizontal: 16,
          bottom: bottom + 16,
        }}
      >
        <PrimaryButton
          variant={'fill'}
          onPress={handlePressSave}
          color="pink"
          size="xlarge"
        >
          내 정보 저장
        </PrimaryButton>
      </Flex>
    </Flex>
  );
};

const MyInfo = () => {
  const { width } = useScreenSize();
  const { showBackButton, onPressBack } = useHandleBackPressed();

  return (
    <AnalyticsScreen screen="MY_INFO">
      <StackScreen
        title="내 정보"
        hideBackButton={!showBackButton}
        onPressBack={onPressBack}
        rightIcons={[<SearchIcon />, <CartIcon />]}
      >
        <Suspense
          fallback={
            <Flex flex={1} width={width} margin={{ horizontal: 'auto' }}>
              <Flex.Center flex={1}>
                <ActivityIndicator />
              </Flex.Center>
            </Flex>
          }
        >
          <MyInfoContent onClose={onPressBack} />
        </Suspense>
      </StackScreen>
    </AnalyticsScreen>
  );
};

export default MyInfo;

const BIRTHDAY_MIN_DATE = '1900-01-01';

const formatYyyyMmDd = (date: Date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};
